import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { List, OrderedMap } from 'immutable';
import { createSelector } from '@opengov/compose-app/reselect';
import { SortDirection } from '@opengov/reporting-types';
import { FilterOperators } from '@opengov/reporting-types/src/reportFilters';

import { selectBypassColumnMultiFilterLimitFeatureFlag } from '../../../common/flags';
import { getTableColumnTypeFromReportColumnType } from '../../../common/helpers/semanticTypeToColumnType';
import { reportLocaleText } from '../../../common/locale';
import { ReportModalTypes } from '../../../common/modals/types';
import { reportEffects } from '../../../common/report/effects/reportEffects';
import { reportConfigurationEffects } from '../../../common/reportConfiguration/effects/reportConfigurationEffects';
import { selectAgGridColumnConfig } from '../../../common/reportConfiguration/reportColumnSelectors';
import {
  selectActiveTableSort,
  selectDetailTableFilter,
  selectDetailTableFilterBooleanOperators,
  selectDetailTableGroupByColumns
} from '../../../common/reportConfiguration/selectors';
import { reportConfigurationActionCreators } from '../../../common/reportConfiguration/state';
import {
  selectColumnByNameFromCurrentView,
  selectReportTotalRowCount
} from '../../selectors/selectReportExplorerState';

import { applyFilter } from './applyFilter';
import { getFilterState } from './getFilterStates';
import { selectFilterValuesByColumn, selectSelectedAgGridColumn } from './selectSelectedAgGridColumn';
import { selectRouteName } from '../../../common/routes/selectors/routeSelectors';

export const selectFilterPropsRowOperator = createSelector(
  selectReportTotalRowCount,
  selectBypassColumnMultiFilterLimitFeatureFlag,
  (reportRowCount, bypassMultiFilterLimit) => {
    if (bypassMultiFilterLimit) {
      return FilterOperators.Equal;
    }
    return reportRowCount && reportRowCount <= 1000000 ? FilterOperators.Equal : FilterOperators.Contain;
  }
);

export const selectFilterPropsAdditional = createSelector(
  reportLocaleText,
  selectDetailTableGroupByColumns,
  (reportText, groupByColumns) => ({
    reportText,
    groupByColumns
  })
);

const selectFilterPropsColumnInfo = createSelector(
  selectSelectedAgGridColumn,
  selectColumnByNameFromCurrentView,
  selectAgGridColumnConfig,
  (activeColumnName, selectColumn, agGridColumnConfig) => ({
    activeColumnName,
    selectColumn,
    agGridColumnConfig
  })
);

const selectFilterPropsFilterConfig = createSelector(
  selectDetailTableFilter,
  selectDetailTableFilterBooleanOperators,
  selectFilterPropsRowOperator,
  selectReportTotalRowCount,
  selectBypassColumnMultiFilterLimitFeatureFlag,
  (filters, filterBooleanOperators, rowOperator, rowCount, bypassMultiFilterLimit) => ({
    filters,
    filterBooleanOperators,
    rowOperator,
    rowCount,
    bypassMultiFilterLimit
  })
);

export const selectFilterProps = createSelector(
  selectFilterPropsColumnInfo,
  selectActiveTableSort,
  selectRouteName,
  selectFilterValuesByColumn,
  selectFilterPropsFilterConfig,
  selectFilterPropsAdditional,
  (
    { activeColumnName, selectColumn, agGridColumnConfig },
    sort,
    routeName,
    filterValuesByColumn,
    { filters, filterBooleanOperators, rowOperator, rowCount, bypassMultiFilterLimit },
    { reportText, groupByColumns }
  ) => {
    const activeColumn = selectColumn(activeColumnName);
    const agGridConfigForSingleColumn =
      agGridColumnConfig && agGridColumnConfig.find((col) => col.columnName === activeColumnName);
    const columnGroup =
      agGridColumnConfig &&
      agGridConfigForSingleColumn &&
      agGridColumnConfig
        .filter((col) => col.pinned === agGridConfigForSingleColumn.pinned)
        .sort((a, b) => (a.sortedIndex < b.sortedIndex ? -1 : 0));
    const columnIsLeftmost = columnGroup && columnGroup[0].columnName === activeColumnName;
    const columnIsRightmost = columnGroup && columnGroup[columnGroup.length - 1].columnName === activeColumnName;
    const isGroupByColumn = groupByColumns?.[0] === activeColumnName;
    return {
      disableHideColumn: isGroupByColumn,
      disableHideColumnTooltipText: isGroupByColumn ? reportText.reportTable.groupByFilterControlsDisabled : '',
      disablePinLeft: isGroupByColumn,
      disablePinLeftTooltipText: isGroupByColumn ? reportText.reportTable.groupByFilterControlsDisabled : '',
      disablePinRight: isGroupByColumn,
      disablePinRightTooltipText: isGroupByColumn ? reportText.reportTable.groupByFilterControlsDisabled : '',
      disableMoveLeftTooltipText: isGroupByColumn ? reportText.reportTable.groupByFilterControlsDisabled : '',
      disableMoveRightTooltipText: isGroupByColumn ? reportText.reportTable.groupByFilterControlsDisabled : '',
      columnName: activeColumn && activeColumn.name,
      columnType: activeColumn && getTableColumnTypeFromReportColumnType(activeColumn),
      getFilterOptions: (searchString) =>
        reportEffects.performColumnSearchQuery({
          stringToSearchFor: searchString,
          column: activeColumn
        }),
      filterCount: filters && filters.length,
      getFilterState: (columnName) => {
        const column = selectColumn(columnName);
        return getFilterState({
          column,
          defaultOperator: rowOperator,
          filterBooleanOperators: filterBooleanOperators,
          filterOptions: filterValuesByColumn.get(activeColumnName) ?? OrderedMap<string, number>(),
          filters: List(filters),
          rowCount,
          bypassMultiFilterLimit
        });
      },
      showSettingsButton: routeName === `ReportSavedViewEdit`,
      sortColumn: sort && sort.sortColumn,
      sortDirection: sort && (sort.sortDirection as any),
      reportRowCount: rowCount,
      isMultiText: true,
      shouldColumnPinAndReorder: true,
      pinDirection: agGridConfigForSingleColumn && agGridConfigForSingleColumn.pinned,
      isLeftmostColumn: columnIsLeftmost || false,
      isRightmostColumn: columnIsRightmost || false,
      onApplyFilter: (columnName, state) => {
        const column = selectColumn(columnName);
        applyFilter(column, state);
      },
      onClearFilter: (columnName) => {
        reportConfigurationActionCreators.clearColumnFiltersAndSort({
          columnName: columnName
        });
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      },
      onClearSort: () => {
        reportConfigurationActionCreators.clearColumnSort(null);
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      },
      onShowSettings: () => {
        modalActionCreators.setModalComponentType(ReportModalTypes.COLUMN_EDIT);
      },
      onSortAsc: (sortColumn) => {
        reportConfigurationActionCreators.setColumnSort({
          sortColumn,
          sortDirection: SortDirection.ASC
        });
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      },
      onSortDesc: (sortColumn) => {
        reportConfigurationActionCreators.setColumnSort({
          sortColumn,
          sortDirection: SortDirection.DESC
        });
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      }
    };
  }
);
